import { Toaster } from "@jl/components/ui/toaster";
import { Toaster as Sonner } from "@jl/components/ui/sonner";
import { TooltipProvider } from "@jl/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./index.css";
import { LanguageProvider } from "@jl/contexts/LanguageContext";
import { AuthProvider } from "@jl/contexts/AuthContext";
import { Header } from "@jl/components/Header";
import { RequireStaff } from "@jl/components/RequireStaff";
import { ScrollToTop } from "@jl/components/ScrollToTop";
import Index from "./pages/Index";
import Browse from "./pages/Browse";
import Contribute from "./pages/Contribute";
import Search from "./pages/Search";
import TermDetail from "./pages/TermDetail";
import Login from "./pages/Login";
import StaffLogin from "./pages/StaffLogin";
import ForgotPassword from "./pages/ForgotPassword";
import Account from "./pages/Account";
import Admin from "./pages/Admin";
import ScopeOfWork from "./pages/ScopeOfWork";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

type JainLexiconAppProps = {
  basename?: string;
};

const App = ({ basename }: JainLexiconAppProps) => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <LanguageProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter basename={basename}>
            <ScrollToTop />
            <div className="flex min-h-screen flex-col">
              <Header />
              <main className="min-w-0 flex-1 overflow-x-clip">
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/browse" element={<Browse />} />
                  <Route path="/contribute" element={<Contribute />} />
                  <Route path="/search" element={<Search />} />
                  <Route path="/term/:id" element={<TermDetail />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/staff-login" element={<StaffLogin />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route
                    path="/staff-forgot-password"
                    element={<ForgotPassword />}
                  />
                  <Route path="/account" element={<Account />} />
                  <Route path="/admin" element={<RequireStaff />}>
                    <Route index element={<Navigate to="reports" replace />} />
                    <Route path=":section" element={<Admin />} />
                  </Route>
                  <Route
                    path="/my-contributions"
                    element={
                      <Navigate to="/contribute#contributions" replace />
                    }
                  />
                  <Route path="/scope-of-work" element={<ScopeOfWork />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </main>
            </div>
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
